import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, GraduationCap, CalendarDays, MapPin, Clock, UserMinus } from 'lucide-react';
import DatePickerField from './DatePickerField';
import { WalkInPicker } from './TrainingCardsParts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const fmtDay = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
export const fmtRange = (s, e) => (!e || e === s) ? fmtDay(s) : `${fmtDay(s)} – ${fmtDay(e)}`;
export const fmtTime = (t) => { if (!t) return ''; const [h, m] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };

// Same rule as the backend: `days` weekdays from the start date (a Friday 2-day course = Fri + Mon).
export const trainingDates = (start, days) => {
  if (!start) return [];
  const out = [start];
  const d = new Date(`${start}T12:00:00`);
  while (out.length < Math.max(1, days)) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) out.push(iso(d));
  }
  return out;
};

const inputCls = 'w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-blue-500';

// Confirm + details step after ticking people in the trainer report. Books a new session or adds them to an upcoming one.
export default function TrainingScheduleModal({ provider, people, defaultDays, existing, onClose, onDone }) {
  const [mode, setMode] = useState(existing?.length ? `add:${existing[0].id}` : 'new');
  const [startDate, setStartDate] = useState('');
  const [days, setDays] = useState(defaultDays || 1);
  const [startTime, setStartTime] = useState('07:00');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState(people);
  const [pool, setPool] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/training-sessions/people`).then((r) => setPool(r.data.people)).catch(() => {}); }, []);
  const addAnyone = (p) => setRows((x) => [...x, { employee_id: p.employee_id, name: p.name, division: p.division, courses: [], added: true }]);
  const today = iso(new Date());
  const dates = useMemo(() => trainingDates(startDate, Number(days) || 1), [startDate, days]);
  const target = mode.startsWith('add:') ? existing.find((s) => s.id === mode.slice(4)) : null;

  const submit = async () => {
    if (!rows.length) { toast.error('Nobody left to book'); return; }
    if (!target && !startDate) { toast.error('Pick a start date'); return; }
    setSaving(true);
    try {
      const attendees = rows.map((r) => ({ employee_id: r.employee_id, courses: r.courses.map((c) => ({ topic: c.topic, label: c.label, status: c.status, expiry: c.expiry })) }));
      if (target) {
        await axios.post(`${API}/training-sessions/${target.id}/attendees`, { attendees });
        toast.success(`Added ${rows.length} to the ${fmtRange(target.start_date, target.end_date)} session — they've been messaged and blocked out`);
      } else {
        await axios.post(`${API}/training-sessions`, { provider_id: provider.id, start_date: startDate, days: Number(days) || 1, start_time: startTime, location, notes, attendees });
        toast.success(`Booked ${rows.length} ${rows.length === 1 ? 'person' : 'people'} with ${provider.name} — chat opened, schedule blocked ${fmtRange(dates[0], dates[dates.length - 1])}`);
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not book the training'); }
    setSaving(false);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="training-schedule-modal">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
          <div>
            <h3 className="text-slate-900 font-bold text-base md:text-lg flex items-center gap-2"><GraduationCap className="w-5 h-5 text-purple-700" /> Schedule training — {provider.name}</h3>
            <p className="text-xs text-slate-500 mt-1">Attendees get a chat message + push, a card on their dashboard, and they're blocked out on the Millwright Schedule for those days.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50" data-testid="training-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {existing?.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="training-mode">
              {existing.map((s) => (
                <button key={s.id} onClick={() => setMode(`add:${s.id}`)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${mode === `add:${s.id}` ? 'bg-purple-600 border-purple-500 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid={`training-mode-add-${s.id}`}>
                  Add to {fmtRange(s.start_date, s.end_date)} ({s.attendees.length} booked)
                </button>
              ))}
              <button onClick={() => setMode('new')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${mode === 'new' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'}`} data-testid="training-mode-new">New session</button>
            </div>
          )}
          {!target && (
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block sm:col-span-1">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Start date</span>
                <DatePickerField value={startDate} onChange={setStartDate} min={today} className="mt-1 w-full bg-white rounded-lg px-3 py-2 text-sm" testid="training-start-date" clearable={false} />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Days</span>
                <input type="number" min={1} max={10} value={days} onChange={(e) => setDays(e.target.value)} className={`${inputCls} mt-1`} data-testid="training-days" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Start time</span>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={`${inputCls} mt-1`} data-testid="training-start-time" />
              </label>
              <label className="block sm:col-span-3">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location</span>
                <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. 365 shop boardroom / trainer's site" className={`${inputCls} mt-1`} data-testid="training-location" />
              </label>
              <label className="block sm:col-span-3">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Notes to the attendees</span>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Bring your PPE, lunch provided…" className={`${inputCls} mt-1`} data-testid="training-notes" />
              </label>
              {dates.length > 0 && (
                <p className="sm:col-span-3 text-sm text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="training-dates-preview">
                  📅 {dates.map(fmtDay).join(' · ')}{dates.length > 1 && new Date(`${dates[dates.length - 1]}T12:00:00`) - new Date(`${dates[0]}T12:00:00`) > (dates.length - 1) * 86400000 ? ' (weekend skipped)' : ''} · starts {fmtTime(startTime)}
                  {[0, 6].includes(new Date(`${startDate}T12:00:00`).getDay()) && <span className="block text-amber-700 mt-0.5" data-testid="training-weekend-warning">⚠️ The first day is a weekend — is that right?</span>}
                </p>
              )}
            </div>
          )}
          {target && (
            <p className="text-sm text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2" data-testid="training-add-target">
              📅 {fmtRange(target.start_date, target.end_date)} · starts {fmtTime(target.start_time)}{target.location ? ` · ${target.location}` : ''}
            </p>
          )}
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Going ({rows.length})</p>
              <WalkInPicker pool={pool} taken={new Set(rows.map((r) => r.employee_id))} onAdd={addAnyone} placeholder="Add anyone…" testid="training-add-anyone" />
            </div>
            {rows.length === 0 && <p className="text-xs text-slate-500 mb-1" data-testid="training-nobody">Nobody yet — use “Add anyone” to put people on this session.</p>}
            <div className="grid gap-1 sm:grid-cols-2" data-testid="training-attendees">
              {rows.map((r) => (
                <div key={r.employee_id} className="flex items-center justify-between gap-2 text-sm px-3 py-1.5 rounded-lg bg-white border border-slate-200" data-testid={`training-attendee-${r.employee_id}`}>
                  <span className="min-w-0">
                    <span className="text-slate-900 font-medium">{r.name}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{r.courses.map((c) => c.label).join(', ') || (r.added ? `All ${provider.name} courses` : provider.name)}</span>
                  </span>
                  <button onClick={() => setRows((x) => x.filter((y) => y.employee_id !== r.employee_id))} className="p-1 rounded text-slate-500 hover:text-red-700" title="Leave this person out" data-testid={`training-attendee-remove-${r.employee_id}`}><UserMinus className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-800 hover:bg-slate-100" data-testid="training-cancel-btn">Back</button>
          <button onClick={submit} disabled={saving || !rows.length || (!target && !startDate)} className="px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white" data-testid="training-submit-btn">
            {saving ? 'Booking…' : target ? `Add ${rows.length} to session` : `Book ${rows.length} ${rows.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>, document.body);
}
